import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { formatDateOnly, formatTimeOfDay } from '../../core/datetime/ist';
import { AuthService } from '../../core/services/auth.service';
import { OpsService, PendingResolution } from '../../core/services/ops.service';

interface CaseAttachmentView {
  id: string;
  fileName: string;
  kind: string;
  sizeBytes?: number;
  uploadedAt?: string;
}

@Component({
  selector: 'app-resolution',
  standalone: true,
  imports: [FormsModule, RouterLink, DatePipe],
  templateUrl: './resolution.component.html',
  styleUrl: './resolution.component.scss',
})
export class ResolutionComponent {
  private readonly ops = inject(OpsService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  readonly pending = this.ops.pendingResolutionList;
  readonly closed = this.ops.resolutionList;
  readonly selectedId = signal<string | number>('');
  readonly notes = signal('');
  readonly message = signal<string | null>(null);
  readonly busy = signal(false);
  readonly attachments = signal<CaseAttachmentView[]>([]);

  /** DM issues final orders and hearing decisions. */
  readonly canDecide = computed(() => this.auth.hasRole('dm', 'admin'));

  readonly selected = computed((): PendingResolution | undefined => {
    const list = this.pending();
    if (!list.length) return undefined;
    return list.find((p) => String(p.id) === String(this.selectedId())) ?? list[0];
  });

  readonly nextStep = computed(() => {
    const item = this.selected();
    if (!item) return 'Select a case from the queue.';
    switch (item.status) {
      case 'routed':
      case 'under_review':
        return 'Waiting for the assigned officer to submit an investigation report before you can request a hearing or issue a final order.';
      case 'investigation_completed':
        return 'Officer report is in — send this case to Peshkar for a hearing, or issue a final order when ready.';
      case 'awaiting_hearing':
        return 'Waiting for Peshkar to schedule a hearing slot. Open Hearing Management when booked.';
      case 'scheduled':
        return 'Hearing is booked — open Hearing Management for slot details, then mark conducted. Peshkar can also add another hearing for this case.';
      case 'heard':
        return 'Hearing complete — issue final resolution, or request another hearing if the matter needs a further sitting.';
      default:
        return 'Review the case and choose the appropriate action below.';
    }
  });

  constructor() {
    this.selectedId.set(this.route.snapshot.queryParamMap.get('case') || '');
    effect(() => {
      const item = this.selected();
      if (item) void this.loadAttachments(item.id);
      else this.attachments.set([]);
    });
  }

  statusLabel(status?: string | null): string {
    const key = status || '';
    const map: Record<string, string> = {
      routed: 'Under investigation',
      under_review: 'Under review',
      investigation_completed: 'Report ready',
      awaiting_hearing: 'Sent to Peshkar',
      scheduled: 'Hearing scheduled',
      heard: 'Hearing done',
      resolved: 'Resolved',
      closed: 'Closed',
    };
    return map[key] || (key ? key.replace(/_/g, ' ') : 'Unknown');
  }

  formatHearing(date?: string | null, time?: string | null): string {
    const d = formatDateOnly(date);
    const t = formatTimeOfDay(time);
    if (d === '—' && t === '—') return '—';
    if (t === '—') return d;
    return `${d} · ${t}`;
  }

  statusTone(status?: string | null): string {
    if (status === 'awaiting_hearing') return 'tone-wait';
    if (status === 'scheduled') return 'tone-info';
    if (status === 'heard' || status === 'investigation_completed') return 'tone-ready';
    if (status === 'routed' || status === 'under_review') return 'tone-active';
    return 'tone-neutral';
  }

  fileKindClass(kind?: string): string {
    const k = (kind || '').toLowerCase();
    if (k === 'pdf') return 'is-pdf';
    if (k === 'doc' || k === 'docx') return 'is-doc';
    return 'is-img';
  }

  formatSize(bytes?: number): string {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private async loadAttachments(caseId: string | number): Promise<void> {
    try {
      const items = await this.ops.listComplaintDocuments(caseId);
      this.attachments.set(
        items.map((d) => ({
          id: d.id,
          fileName: d.fileName,
          kind: d.kind || (d.fileName?.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Image'),
          sizeBytes: d.sizeBytes,
          uploadedAt: d.uploadedAt,
        })),
      );
    } catch {
      this.attachments.set([]);
    }
  }

  async openAttachment(file: CaseAttachmentView): Promise<void> {
    try {
      await this.ops.downloadCaseAttachment(file.id, file.fileName);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Could not open attachment');
    }
  }

  /** After investigation report, or after a hearing when another sitting is needed. */
  canNeedHearing(item?: PendingResolution | null): boolean {
    if (!item) return false;
    if (item.status === 'heard') return true;
    return item.status === 'investigation_completed' && !!String(item.notes || '').trim();
  }

  needHearingLabel(item?: PendingResolution | null): string {
    return item?.status === 'heard' ? 'Need Another Hearing → Peshkar' : 'Need Hearing → Peshkar';
  }

  canResolve(status?: string | null): boolean {
    return status === 'investigation_completed' || status === 'heard';
  }

  async issueOrder(close: boolean): Promise<void> {
    if (!this.canDecide()) {
      this.message.set('Only DM can issue final resolution.');
      return;
    }
    const item = this.selected();
    if (!item) return;
    this.busy.set(true);
    try {
      this.message.set(await this.ops.saveResolution(item.id, this.notes() || item.notes, close));
      this.notes.set('');
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Save failed');
    } finally {
      this.busy.set(false);
    }
  }

  async needHearing(): Promise<void> {
    if (!this.canDecide()) {
      this.message.set('Only DM can request a hearing.');
      return;
    }
    const item = this.selected();
    if (!item) return;
    if (!this.canNeedHearing(item)) {
      this.message.set('Wait until the assigned officer submits an investigation report.');
      return;
    }
    this.busy.set(true);
    try {
      this.message.set(await this.ops.requestHearing(item.id, this.notes()));
      this.notes.set('');
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Request hearing failed');
    } finally {
      this.busy.set(false);
    }
  }

  async markHeard(): Promise<void> {
    if (!this.canDecide()) {
      this.message.set('Only DM can mark hearing completed.');
      return;
    }
    const item = this.selected();
    if (!item) return;
    this.busy.set(true);
    try {
      this.message.set(await this.ops.markHeard(item.id, this.notes()));
      this.notes.set('');
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Mark heard failed');
    } finally {
      this.busy.set(false);
    }
  }

  download(): void {
    // Final-order PDF export is not wired yet — avoid a misleading success toast.
  }
}
